import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { BoostArrow, BoostMark } from '../../components/ui/BoostMark';
import { copy } from '../../config/copy';
import { Modal } from '../albums/Modal';
import { rooms } from '../rooms/api';
import { useLiveHosts } from '../rooms/useLiveHosts';

/**
 * What an earned boost is, and how to get one.
 *
 * ## Why it needs explaining at all
 *
 * Every other number on this site is a count of something obvious - plays,
 * saves, followers. This one is a currency with a rule behind it, and nobody
 * guesses "get hosts to play tracks you send them" from a progress bar. Until
 * this existed the only explanation lived in a sentence on the dashboard card,
 * which somebody has to already be looking at.
 *
 * ## The banner says what it is, so the prose does not have to
 *
 * It opened on a title bar reading "Vibed Boosts" above a paragraph beginning
 * "A Vibed Boost is…", which is two lines of the dialog spent restating the
 * thing somebody just clicked. The header now carries the claim - the boost
 * nobody can buy - and the body starts at what it does. A currency worth
 * earning should look like something when you open it.
 *
 * ## The rules come from the server
 *
 * Every figure quoted here - what a play is worth, the threshold, the cooldown,
 * the daily ceiling - is in the response. The same argument as
 * `Weighting::legend()`: copy that explains a rule must not be able to describe
 * one the server does not apply, and hardcoding "10 points" here would survive
 * exactly until somebody tuned the constant.
 *
 * ## It ends by pointing at a room
 *
 * The whole thing is unearnable without a live host, so the last thing it says
 * is whether there is one - and if there is, it stops explaining and gets out
 * of the way.
 */
export function BoostsDialog({ onClose }: { onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['my-boosts'], queryFn: () => rooms.boosts() });
  const { count: liveNow } = useLiveHosts();

  const name = copy['noun.boost.earned'];

  if (!data) {
    return (
      <Modal title={copy['noun.boost.earned.plural']} onClose={onClose} banner={<Banner />}>
        <p className="py-6 text-center text-sm text-soft">Looking…</p>
      </Modal>
    );
  }

  const pct = Math.min(100, Math.round((data.toward_next / data.threshold) * 100));
  const playsPerBoost = Math.round(data.threshold / data.rules.per_play);

  return (
    <Modal title={copy['noun.boost.earned.plural']} onClose={onClose} banner={<Banner />}>
      <div className="flex flex-col gap-6" data-boosts-dialog>
        {/* ---------------------------------------------------------- */}
        {/* Where you are                                               */}
        {/* ---------------------------------------------------------- */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-3xl font-semibold tracking-tight text-ink">
              {data.available > 0 ? (
                <>
                  {data.available}{' '}
                  <span className="text-base font-normal text-soft">
                    ready to spend
                  </span>
                </>
              ) : (
                <>
                  {data.toward_next}
                  <span className="text-lg font-normal text-faint"> / {data.threshold}</span>
                </>
              )}
            </span>
            {data.available > 0 && (
              <span className="meta-caps text-faint">
                hold up to {data.max_held}
              </span>
            )}
          </div>

          <div
            className="h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-track"
            role="progressbar"
            aria-valuenow={data.toward_next}
            aria-valuemin={0}
            aria-valuemax={data.threshold}
            aria-label={`Progress toward your next ${name}`}
          >
            <div
              className="h-full rounded-[var(--radius-pill)] bg-fresh transition-[width] duration-500"
              style={{ width: `${data.available > 0 ? 100 : pct}%` }}
            />
          </div>
        </div>

        {/* ---------------------------------------------------------- */}
        {/* What it is. The banner has already said it cannot be bought */}
        {/* ---------------------------------------------------------- */}
        <p className="text-sm leading-relaxed text-pretty text-soft">
          Spend one when you submit a track to a live host and it goes into their queue{' '}
          <span className="text-ink">above every paid boost</span> — only the host's own pick
          outranks it. They never expire.
        </p>

        {/* ---------------------------------------------------------- */}
        {/* How to earn one                                             */}
        {/* ---------------------------------------------------------- */}
        <div className="flex flex-col gap-3">
          <span className="meta-caps text-faint">How you earn them</span>

          <p className="text-sm leading-relaxed text-pretty text-soft">
            Send <span className="text-ink">other artists'</span> tracks to hosts who are on air.
            Every one a host plays through earns you {data.rules.per_play} points, and{' '}
            {data.threshold} points is a {name} — about {playsPerBoost} good calls.
          </p>

          {/* The exclusions, said plainly. Somebody who works out a rule by
              watching a counter not move learns to distrust the counter. */}
          <ul className="flex flex-col gap-2 border-l border-line pl-4">
            <Rule>Your own tracks never count. This rewards finding, not posting.</Rule>
            <Rule>
              If the host skips it early, it does not count — they have to play it through.
            </Rule>
            <Rule>
              One artist pays out once every {data.rules.artist_cooldown_days} days, so a single
              friend's catalog is not a shortcut.
            </Rule>
            <Rule>
              Up to {data.rules.daily_cap / data.rules.per_play} plays a day count, so nobody
              earns one in an afternoon.
            </Rule>
          </ul>
        </div>

        {/* ---------------------------------------------------------- */}
        {/* What you already earned                                     */}
        {/* ---------------------------------------------------------- */}
        {data.recent.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="meta-caps text-faint">Recently</span>
            <ul className="flex flex-col gap-1.5">
              {data.recent.map((credit, index) => (
                <li
                  key={`${credit.at}-${index}`}
                  className="flex items-baseline justify-between gap-3 font-mono text-[11px]"
                >
                  <span className="min-w-0 truncate text-faint">
                    {credit.host ? (
                      <>
                        <Link
                          to={`/${credit.host_username}`}
                          onClick={onClose}
                          className="text-soft hover:text-ink hover:underline"
                        >
                          {credit.host}
                        </Link>{' '}
                        played {credit.track ?? 'a track you sent'}
                      </>
                    ) : (
                      (credit.track ?? 'a track you sent')
                    )}
                  </span>
                  <span className="shrink-0 text-fresh">+{credit.points}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ---------------------------------------------------------- */}
        {/* Go and do it                                                */}
        {/* ---------------------------------------------------------- */}
        <div className="border-t border-line pt-4">
          {liveNow > 0 ? (
            <p className="text-sm text-soft">
              <span className="text-fresh">
                {liveNow === 1 ? '1 host is' : `${liveNow} hosts are`} on air right now.
              </span>{' '}
              Open the live count in the header to find them.
            </p>
          ) : (
            <p className="text-sm text-soft">
              Nobody is on air at the moment. When somebody goes live they appear in the header,
              and their profile is where you submit.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * The header.
 *
 * ## Why it is not a state
 *
 * It says the same thing whether you hold six or none, because it is describing
 * what the currency is rather than how much of it you have - that is the first
 * line of the body, three inches below. A hero that changed with the count
 * would put the number on screen twice and leave the definition with nowhere to
 * live.
 *
 * ## Built out of tokens, including the greens
 *
 * The ramp `fresh-deep → fresh-mid → fresh` already existed for the loader, and
 * every wash and edge here is a variable. Nothing is a hex, so this survives the
 * light theme - where the greens invert from mint-on-black to a dark green on a
 * pale ground and the whole panel is still legible.
 *
 * The meter bars are the mark's own bars, drawn large, with the arrow floating
 * above them: the icon exploded across the header rather than stamped in a
 * corner of it.
 */
function Banner() {
  return (
    <div className="relative h-40 w-full overflow-hidden bg-fresh-wash sm:h-44">
      {/* The ground: lit from the top left, falling away to the dialog's own
          surface at the bottom right so the body reads as a continuation. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(130% 170% at 18% 0%, color-mix(in srgb, var(--color-fresh) 18%, var(--color-fresh-wash)) 0%, var(--color-fresh-wash) 45%, var(--color-surface) 100%)',
        }}
      />

      {/* Ruled like a level meter's ticks, at an opacity that is felt rather
          than seen - it stops the gradient reading as an empty smear. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            'linear-gradient(to right, color-mix(in srgb, var(--color-fresh) 10%, transparent) 1px, transparent 1px)',
          backgroundSize: '44px 100%',
        }}
      />

      {/* ------------------------------------------------------------ */}
      {/* The mark, at size                                             */}
      {/* ------------------------------------------------------------ */}
      {/*
        A peak rather than the ramp the asset sheet drew, for two reasons. It is
        the shape of the mark itself, so the header is the icon enlarged instead
        of a second arrangement of the same parts. And the tallest bar ends up
        in the middle, which is the only place the arrow can stand without
        touching the close button in the corner above it.

        Inset top and bottom, because the heights are percentages: a container
        only as tall as its contents gives them nothing to be a percentage of,
        and all five resolve to zero.
      */}
      <div aria-hidden className="absolute inset-y-0 right-6 w-[92px] sm:right-8">
        <div className="absolute inset-0 flex items-end justify-between">
          <Bar height="46%" className="bg-fresh-deep" />
          <Bar height="59%" className="bg-fresh-mid" />
          <Bar height="76%" className="bg-fresh" />
          <Bar height="59%" className="bg-fresh-mid" />
          <Bar height="46%" className="bg-fresh-deep" />
        </div>

        {/*
          Anchored to the centre bar rather than to the top of the banner: its
          bottom edge sits two points below that bar's 76%, so it overlaps what
          it rises out of exactly as it does in the glyph, at either height the
          banner is drawn at.
        */}
        <BoostArrow className="absolute bottom-[74%] left-1/2 h-7 w-8 -translate-x-1/2 text-fresh sm:h-8 sm:w-9" />
      </div>

      {/* ------------------------------------------------------------ */}
      {/* What it is                                                    */}
      {/* ------------------------------------------------------------ */}
      <div className="absolute inset-y-0 left-5 right-[7.5rem] flex flex-col justify-center gap-2.5">
        <span className="flex items-center gap-2 text-fresh">
          <BoostMark className="h-5 w-5" />
          <span className="meta-caps">{copy['noun.boost.earned']}</span>
        </span>

        <h2 className="text-[22px] font-semibold leading-[1.15] tracking-tight text-ink sm:text-2xl">
          The boost nobody
          <br />
          can buy.
        </h2>

        {/*
          Two clauses, not three. "above every paid boost" belongs here by
          rights and does not fit: at this width the line wrapped onto the
          floor of the banner. It is the first thing the body says instead,
          in bold, an inch below.
        */}
        <span className="font-mono text-[9px] text-fresh/80 sm:text-[10px]">
          earned by good calls · never expires
        </span>
      </div>
    </div>
  );
}

function Bar({ height, className }: { height: string; className: string }) {
  return (
    <span
      className={`w-2.5 rounded-t-[var(--radius-pill)] sm:w-3 ${className}`}
      style={{ height }}
    />
  );
}

function Rule({ children }: { children: React.ReactNode }) {
  return <li className="text-[13px] leading-relaxed text-soft">{children}</li>;
}
